import V2_SWAP_ROUTER_ABI from 'abis/v2-swap-router.json'
import { TransactionType } from 'state/transactions/types'
import { calculateGasMargin } from 'utils/calculateGasMargin'
import type { Abi, Hex, PublicClient, WriteContractReturnType } from 'viem'
import { Address } from 'viem'

import { DoubleSidedParams, NativeParams, SingleSidedParams, VaultStrategy, WithdrawParams } from './VaultStrategy.type'

export class V2VaultStrategy implements VaultStrategy {
  readonly supportsZapping = false
  readonly supportsAutoCompound = false

  constructor(
    private v2RouterAddress: Address,
    private wagmiPublicClient: PublicClient,
    private writeContractAsync: (args: {
      address: Address
      abi: Abi
      functionName: string
      args?: any[]
      value?: bigint
      gas?: bigint
      account: Address
    }) => Promise<WriteContractReturnType>,
    private chainId: number
  ) {}
  transactionType!: TransactionType

  get routerAddress(): string {
    return this.v2RouterAddress
  }

  // ========================================
  // DOUBLE-SIDED LIQUIDITY (Only supported method)
  // ========================================
  async addLiquidityDoubleSided(params: DoubleSidedParams): Promise<Hex> {
    const { parsedAmountA, parsedAmountB, amount0Min, amount1Min, account, currencyA, currencyB, deadline } = params

    if (currencyA.isNative || currencyB.isNative) {
      return await this.handleNativeDeposit({
        parsedAmountA,
        parsedAmountB,
        amount0Min,
        amount1Min,
        account,
        currencyA,
        currencyB,
        deadline,
      })
    } else {
      return await this.handleTokenDeposit({
        parsedAmountA,
        parsedAmountB,
        amount0Min,
        amount1Min,
        account,
        currencyA,
        currencyB,
        deadline,
      })
    }
  }

  private async handleNativeDeposit(params: {
    parsedAmountA: any
    parsedAmountB: any
    amount0Min: string
    amount1Min: string
    account: string
    currencyA: any
    currencyB: any
    deadline: bigint
  }): Promise<Hex> {
    const { parsedAmountA, parsedAmountB, amount0Min, amount1Min, account, currencyA, currencyB, deadline } = params

    const isNativeA = currencyA.isNative
    const token = isNativeA ? currencyB.wrapped : currencyA.wrapped
    const amountTokenDesired = isNativeA ? parsedAmountB.quotient.toString() : parsedAmountA.quotient.toString()
    const amountETHDesired = isNativeA ? parsedAmountA.quotient.toString() : parsedAmountB.quotient.toString()
    const amountTokenMin = isNativeA ? amount1Min : amount0Min
    const amountETHMin = isNativeA ? amount0Min : amount1Min
    const value = BigInt(amountETHDesired)

    try {
      const tx = {
        address: this.v2RouterAddress,
        abi: V2_SWAP_ROUTER_ABI.abi as Abi,
        functionName: 'addLiquidityETH',
        args: [
          token.address as Address,
          BigInt(amountTokenDesired),
          BigInt(amountTokenMin),
          BigInt(amountETHMin),
          account as Address,
          deadline,
        ],
        value,
        account: account as Address,
      } as const

      const estimatedGas = await this.wagmiPublicClient.estimateContractGas(tx)
      const txHash = await this.writeContractAsync({
        ...tx,
        gas: calculateGasMargin(estimatedGas) as bigint,
      })

      return txHash
    } catch (error: any) {
      console.warn('Gas estimation failed for V2 addLiquidityETH:', error)
      throw error
    }
  }

  private async handleTokenDeposit(params: {
    parsedAmountA: any
    parsedAmountB: any
    amount0Min: string
    amount1Min: string
    account: string
    currencyA: any
    currencyB: any
    deadline: bigint
  }): Promise<Hex> {
    const { parsedAmountA, parsedAmountB, amount0Min, amount1Min, account, currencyA, currencyB, deadline } = params

    try {
      const tx = {
        address: this.v2RouterAddress,
        abi: V2_SWAP_ROUTER_ABI.abi as Abi,
        functionName: 'addLiquidity',
        args: [
          currencyA.wrapped.address as Address,
          currencyB.wrapped.address as Address,
          BigInt(parsedAmountA.quotient.toString()),
          BigInt(parsedAmountB.quotient.toString()),
          BigInt(amount0Min),
          BigInt(amount1Min),
          account as Address,
          deadline,
        ],
        account: account as Address,
      } as const

      const estimatedGas = await this.wagmiPublicClient.estimateContractGas(tx)
      const txHash = await this.writeContractAsync({
        ...tx,
        gas: calculateGasMargin(estimatedGas) as bigint,
      })

      return txHash
    } catch (error: any) {
      console.warn('Gas estimation failed for V2 addLiquidity:', error)
      throw error
    }
  }

  // ========================================
  // WITHDRAW LIQUIDITY
  // ========================================

  async withdrawLiquidity(params: WithdrawParams): Promise<Hex> {
    const { currencyA, currencyB, amount, amount0Min, amount1Min, account, deadline } = params

    try {
      if (currencyA.isNative || currencyB.isNative) {
        const token = currencyA.isNative ? currencyB.wrapped : currencyA.wrapped
        const amountTokenMin = currencyA.isNative ? amount1Min : amount0Min
        const amountETHMin = currencyA.isNative ? amount0Min : amount1Min

        const tx = {
          address: this.v2RouterAddress,
          abi: V2_SWAP_ROUTER_ABI.abi as Abi,
          functionName: 'removeLiquidityETH',
          args: [
            token.address as Address,
            BigInt(amount.quotient.toString()),
            BigInt(amountTokenMin),
            BigInt(amountETHMin),
            account as Address,
            deadline,
          ],
          account: account as Address,
        } as const

        const estimatedGas = await this.wagmiPublicClient.estimateContractGas(tx)
        const txHash = await this.writeContractAsync({
          ...tx,
          gas: calculateGasMargin(estimatedGas) as bigint,
        })

        return txHash
      } else {
        const tx = {
          address: this.v2RouterAddress,
          abi: V2_SWAP_ROUTER_ABI.abi as Abi,
          functionName: 'removeLiquidity',
          args: [
            currencyA.wrapped.address as Address,
            currencyB.wrapped.address as Address,
            BigInt(amount.quotient.toString()),
            BigInt(amount0Min),
            BigInt(amount1Min),
            account as Address,
            deadline,
          ],
          account: account as Address,
        } as const

        const estimatedGas = await this.wagmiPublicClient.estimateContractGas(tx)
        const txHash = await this.writeContractAsync({
          ...tx,
          gas: calculateGasMargin(estimatedGas) as bigint,
        })

        return txHash
      }
    } catch (error) {
      console.error('WithdrawLiquidity failed:', error)
      throw error
    }
  }

  // ========================================
  // UNSUPPORTED METHODS (V2 limitations)
  // ========================================
  async addLiquidityNative(params: NativeParams): Promise<Hex> {
    throw new Error('V2 pools do not support single-sided native deposits. Use double-sided deposits instead.')
  }

  async addLiquiditySingleSided(params: SingleSidedParams): Promise<Hex> {
    throw new Error('V2 pools do not support single-sided deposits. Use double-sided deposits instead.')
  }
}
